/**
 * Phase 17 Plan 17-02 — DeactivateKpiConfirmDialog
 *
 * Fetches the KPI's entryCount via useKpiDetailQuery on open.
 * Renders a confirmation message including the entry count before calling deactivate.
 */

import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDeactivateKpiMutation, useKpiDetailQuery } from '@/hooks/useKpiQueries';
import { toast } from '@/lib/toast';

export interface DeactivateKpiConfirmDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
  readonly workOrderId: number;
  readonly kpiId: number;
  readonly kpiName: string;
}

export function DeactivateKpiConfirmDialog({
  open,
  onOpenChange,
  workOrderId,
  kpiId,
  kpiName,
}: DeactivateKpiConfirmDialogProps): JSX.Element {
  const mutation = useDeactivateKpiMutation();
  const [serverError, setServerError] = useState<string | null>(null);

  // Fetch detail to get entryCount
  const detailQuery = useKpiDetailQuery(workOrderId, kpiId, open);
  const entryCount = detailQuery.data?.entryCount ?? 0;

  function handleClose(): void {
    setServerError(null);
    mutation.reset();
    onOpenChange(false);
  }

  function handleConfirm(): void {
    setServerError(null);
    mutation.mutate(
      { woId: workOrderId, kpiId },
      {
        onSuccess: () => {
          toast.success(`KPI "${kpiName}" deactivated`);
          handleClose();
        },
        onError: (err) => {
          const msg = err.detail ?? err.message;
          setServerError(msg);
          toast.error('Failed to deactivate KPI', { description: msg });
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="deactivate-kpi-dialog">
        <DialogHeader>
          <DialogTitle>Deactivate KPI</DialogTitle>
          <DialogDescription>
            You are about to deactivate <strong>{kpiName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2 text-sm">
          {detailQuery.isLoading && (
            <p className="text-muted-foreground" data-testid="deactivate-kpi-loading">
              Loading KPI details…
            </p>
          )}
          {detailQuery.data !== undefined && (
            <p data-testid="deactivate-kpi-entry-count">
              This KPI has{' '}
              <strong>{entryCount}</strong>{' '}
              {entryCount === 1 ? 'entry' : 'entries'} — soft-deleting will hide it from new
              logsheet entries but preserve history. Confirm?
            </p>
          )}
          {serverError !== null && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="deactivate-kpi-error"
            >
              {serverError}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
            data-testid="deactivate-kpi-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={mutation.isPending || detailQuery.isLoading}
            data-testid="deactivate-kpi-confirm"
          >
            {mutation.isPending ? 'Deactivating…' : 'Deactivate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
